/* eslint-disable @typescript-eslint/no-use-before-define */
import fetch from 'isomorphic-fetch'
import { RSAA } from 'redux-api-middleware'

import { Env, getEnv } from '@cinch/oxd-utils/env/src'
import { UpdatePasswordParams } from '@cinch/oxd-utils/requests/src/identity-service'
import { injectSpanInto, Span } from '@cinch/oxd-utils/tracing/src'

import * as types from './types'

interface RSAAParam {
  endpoint: string
  types: Array<string | { type: string; [key: string]: any }>
  body?: string
  method?: 'GET' | 'PUT' | 'POST' | 'DELETE'
  fetch?: any
  headers?: ((state: any) => Headers) | Headers
}

const userProfileUrl = getEnv(Env.UserServiceUrl)
const indentityServiceUrl = getEnv(Env.IdentityServiceUrl)

const RSAActionCreator = (params: RSAAParam) => ({
  [RSAA]: {
    method: 'GET',
    fetch,
    headers: (state: any) => ({
      Authorization: `Bearer ${state.oidc.user.access_token}`,
      'Content-Type': 'application/json',
    }),
    ...params,
  },
})

export const getProfile = () => {
  let span: Span

  return {
    [RSAA]: {
      endpoint: `${userProfileUrl}/userprofiles`,
      method: 'GET',
      fetch,
      headers: (state: any) => {
        const headers = getAuthHeadersNoCache(state)
        span = injectSpanInto(headers, 'get-user-profile')

        return headers
      },
      types: [
        types.GET_PROFILE_REQUEST,
        {
          type: types.GET_PROFILE_SUCCESS,
          payload: (_action: any, _state: any, response: any) => {
            span.finish()

            return response.json()
          },
        },
        {
          type: types.GET_PROFILE_FAILURE,
          meta: (_action: any, _state: any, response: any) => {
            span.finish()

            return response.json()
          },
        },
      ],
    },
  }
}

export const updateProfile = (payload: Partial<Cinch.UserProfileRequest>) => ({
  type: types.UPDATE_USER_PROFILE,
  payload,
})

export const updateAnonProfile = (payload: Partial<Cinch.UserProfileRequest>) => ({
  type: types.UPDATE_ANON_USER_PROFILE,
  payload,
})

export const updateLoggedInProfile = (body: Partial<Cinch.UserProfileRequest>) => {
  let span: Span

  return {
    [RSAA]: {
      endpoint: `${userProfileUrl}/userprofiles`,
      method: 'PUT',
      fetch,
      body: ({ profile }: any) =>
        JSON.stringify({
          ...profile.data,
          ...body,
        }),
      headers: (state: any) => {
        const headers = getAuthHeaders(state)
        span = injectSpanInto(headers, 'put-user-profile')

        return headers
      },
      types: [
        types.UPDATE_PROFILE_REQUEST,
        {
          type: types.UPDATE_PROFILE_SUCCESS,
          payload: (action: any) => {
            span.finish()

            return JSON.parse(action[RSAA].body({ profile: { data: {} } }))
          },
        },
        {
          type: types.UPDATE_PROFILE_FAILURE,
          meta: (_action: any, _state: any, response: any) => {
            span.finish()

            return response.json()
          },
        },
      ],
    },
  }
}

export const removePasswordError = () => ({
  type: types.CHANGE_PASSWORD_REMOVE_ERROR,
})

export const changePassword = (
  updatePasswordData: Pick<UpdatePasswordParams, Exclude<keyof UpdatePasswordParams, 'userId'>>,
) => {
  let span: Span

  return {
    [RSAA]: {
      endpoint: `${indentityServiceUrl}/password`,
      method: 'PUT',
      fetch,
      body: JSON.stringify(updatePasswordData),
      headers: (state: any) => {
        const headers = getAuthHeaders(state)
        span = injectSpanInto(headers, 'put-password')

        return headers
      },
      types: [
        types.CHANGE_PASSWORD_REQUEST,
        {
          type: types.CHANGE_PASSWORD_SUCCESS,
          payload: (_action: any, _state: any, response: any) => {
            span.finish()

            return response.json()
          },
        },
        {
          type: types.CHANGE_PASSWORD_FAILURE,
          meta: (_action: any, _state: any, response: any) => {
            span.finish()

            return response
          },
        },
      ],
    },
  }
}

const createSavedSearch = (name: string, filters: Cinch.FilterFormData, location: string) => ({
  name,
  location,
  bodyType: filters.bodyType,
  colour: filters.colour,
  distance: filters.distance,
  doors: filters.doors,
  fromEngineSize: filters.fromEngineSize,
  toEngineSize: filters.toEngineSize,
  fuelType: filters.fuelType,
  keywords: filters.keywords,
  latitude: filters.latitude,
  longitude: filters.longitude,
  make: filters.make,
  mileage: filters.mileage,
  fromPrice: filters.fromPrice,
  toPrice: filters.toPrice,
  selectedModel: filters.selectedModel,
  transmissionType: filters.transmissionType,
  fromYear: filters.fromYear,
  toYear: filters.toYear,
})

export const saveSearch = (name: string, filters: Cinch.FilterFormData, location: string) => ({
  type: types.SAVE_SEARCH,
  payload: createSavedSearch(name, filters, location),
})

export const saveSearchLoggedIn = (name: string, filters: Cinch.FilterFormData, location: string) => {
  const search = createSavedSearch(name, filters, location)

  return {
    [RSAA]: {
      endpoint: `${userProfileUrl}/userprofiles/searches`,
      method: 'POST',
      body: JSON.stringify(search),
      fetch,
      headers: (state: any) => ({
        Authorization: `Bearer ${state.oidc.user.access_token}`,
        'Content-Type': 'application/json',
      }),
      types: [
        types.SAVE_SEARCH_REQUEST,
        {
          type: types.SAVE_SEARCH_SUCCESS,
          payload: async () => {
            // TODO: return response id once backend updated.
            return null
          },
        },
        types.SAVE_SEARCH_FAILURE,
      ],
    },
  }
}

export const removeSavedSearch = (searchId: string) => ({
  type: types.REMOVE_SAVED_SEARCH,
  payload: searchId,
})

export const removeSavedSearchLoggedIn = (searchSavedId: string) => {
  return {
    [RSAA]: {
      endpoint: `${userProfileUrl}/userprofiles/searches/${searchSavedId}`,
      method: 'DELETE',
      fetch,
      headers: (state: any) => ({
        Authorization: `Bearer ${state.oidc.user.access_token}`,
        'Content-Type': 'application/json',
      }),
      types: [
        types.REMOVE_SAVED_SEARCH_REQUEST,
        {
          type: types.REMOVE_SAVED_SEARCH_SUCCESS,
          payload: { searchSavedId },
        },
        types.REMOVE_SAVED_SEARCH_FAILURE,
      ],
    },
  }
}

export const addFavourite = (vehicleId: number) => ({
  type: types.ADD_FAVOURITE,
  payload: vehicleId,
})

export const addDirectFavourite = (vehicleId: string) => ({
  type: types.ADD_DIRECT_FAVOURITE,
  payload: vehicleId,
})

export const removeFavourite = (vehicleId: number) => ({
  type: types.REMOVE_FAVOURITE,
  payload: vehicleId,
})

export const removeDirectFavourite = (vehicleId: string) => ({
  type: types.REMOVE_DIRECT_FAVOURITE,
  payload: vehicleId,
})

export const addLoggedInFavourite = (vehicleId: number, isLoginFavourite = false) => ({
  [RSAA]: {
    endpoint: `${userProfileUrl}/userprofiles/favourites`,
    method: 'POST',
    body: JSON.stringify({ vehicleId }),
    fetch,
    headers: (state: any) => ({
      Authorization: `Bearer ${state.oidc.user.access_token}`,
      'Content-Type': 'application/json',
    }),
    types: [
      { type: types.ADD_FAVOURITE_REQUEST, payload: { vehicleId } },
      {
        type: types.ADD_FAVOURITE_SUCCESS,
        payload: { vehicleId, createdAt: new Date().toJSON() },
        meta: { isLoginFavourite },
      },
      types.ADD_FAVOURITE_FAILURE,
    ],
  },
})

export const addLoggedInDirectFavourite = (vehicleId: string, isLoginFavourite = false) => ({
  [RSAA]: {
    endpoint: `${userProfileUrl}/userprofiles/directfavourites`,
    method: 'POST',
    body: JSON.stringify({ vehicleId }),
    fetch,
    headers: (state: any) => ({
      Authorization: `Bearer ${state.oidc.user.access_token}`,
      'Content-Type': 'application/json',
    }),
    types: [
      { type: types.ADD_DIRECT_FAVOURITE_REQUEST, payload: { vehicleId } },
      {
        type: types.ADD_DIRECT_FAVOURITE_SUCCESS,
        payload: { vehicleId, createdAt: new Date().toJSON() },
        meta: { isLoginFavourite },
      },
      types.ADD_DIRECT_FAVOURITE_FAILURE,
    ],
  },
})

export const removeLoggedInFavourite = (vehicleId: number) => ({
  [RSAA]: {
    endpoint: `${userProfileUrl}/userprofiles/favourites/${vehicleId}`,
    method: 'DELETE',
    fetch,
    headers: (state: any) => ({
      Authorization: `Bearer ${state.oidc.user.access_token}`,
      'Content-Type': 'application/json',
    }),
    types: [
      types.REMOVE_FAVOURITE_REQUEST,
      {
        type: types.REMOVE_FAVOURITE_SUCCESS,
        payload: { vehicleId },
      },
      types.REMOVE_FAVOURITE_FAILURE,
    ],
  },
})

export const removeLoggedInDirectFavourite = (vehicleId: string) => ({
  [RSAA]: {
    endpoint: `${userProfileUrl}/userprofiles/directfavourites/${vehicleId}`,
    method: 'DELETE',
    fetch,
    headers: (state: any) => ({
      Authorization: `Bearer ${state.oidc.user.access_token}`,
      'Content-Type': 'application/json',
    }),
    types: [
      types.REMOVE_DIRECT_FAVOURITE_REQUEST,
      {
        type: types.REMOVE_DIRECT_FAVOURITE_SUCCESS,
        payload: { vehicleId },
      },
      types.REMOVE_DIRECT_FAVOURITE_FAILURE,
    ],
  },
})

export const removeFavourites = (vehicles: number[] = []) => ({
  [RSAA]: {
    endpoint: `${userProfileUrl}/userprofiles/favourites/?vehicleIds=${vehicles.join(',')}`,
    method: 'DELETE',
    fetch,
    headers: (state: any) => ({
      Authorization: `Bearer ${state.oidc.user.access_token}`,
      'Content-Type': 'application/json',
    }),
    types: [
      types.REMOVE_FAVOURITES_REQUEST,
      {
        type: types.REMOVE_FAVOURITES_SUCCESS,
        payload: { vehicles },
      },
      types.REMOVE_FAVOURITES_FAILURE,
    ],
  },
})

export const removeDirectFavourites = (vehicles: string[] = []) => ({
  [RSAA]: {
    endpoint: `${userProfileUrl}/userprofiles/directfavourites/?vehicleIds=${vehicles.join(',')}`,
    method: 'DELETE',
    fetch,
    headers: (state: any) => ({
      Authorization: `Bearer ${state.oidc.user.access_token}`,
      'Content-Type': 'application/json',
    }),
    types: [
      types.REMOVE_DIRECT_FAVOURITES_REQUEST,
      {
        type: types.REMOVE_DIRECT_FAVOURITES_SUCCESS,
        payload: { vehicles },
      },
      types.REMOVE_DIRECT_FAVOURITES_FAILURE,
    ],
  },
})

export const getFavourites = () => {
  let span: Span

  return {
    [RSAA]: {
      endpoint: `${userProfileUrl}/userprofiles/favourites`,
      method: 'GET',
      fetch,
      headers: (state: any) => {
        const headers = getAuthHeadersNoCache(state)
        span = injectSpanInto(headers, 'get-favourites')

        return headers
      },
      types: [
        types.GET_FAVOURITES_REQUEST,
        {
          type: types.GET_FAVOURITES_SUCCESS,
          payload: (_action: any, _state: any, response: any) => {
            span.finish()

            return response.json()
          },
        },
        {
          type: types.GET_FAVOURITES_FAILURE,
          meta: (_action: any, _state: any, response: any) => {
            span.finish()

            return response.json()
          },
        },
      ],
    },
  }
}

export const getDirectFavourites = () => {
  let span: Span

  return {
    [RSAA]: {
      endpoint: `${userProfileUrl}/userprofiles/directfavourites`,
      method: 'GET',
      fetch,
      headers: (state: any) => {
        const headers = getAuthHeadersNoCache(state)
        span = injectSpanInto(headers, 'get-favourites')

        return headers
      },
      types: [
        types.GET_DIRECT_FAVOURITES_REQUEST,
        {
          type: types.GET_DIRECT_FAVOURITES_SUCCESS,
          payload: (_action: any, _state: any, response: any) => {
            span.finish()

            return response.json()
          },
        },
        {
          type: types.GET_DIRECT_FAVOURITES_FAILURE,
          meta: (_action: any, _state: any, response: any) => {
            span.finish()

            return response.json()
          },
        },
      ],
    },
  }
}

export const getSavedSearches = () => ({
  [RSAA]: {
    endpoint: `${userProfileUrl}/userprofiles/searches`,
    method: 'GET',
    fetch,
    headers: (state: any) => ({
      Authorization: `Bearer ${state.oidc.user.access_token}`,
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    }),
    types: [types.GET_SAVED_SEARCHES_REQUEST, types.GET_SAVED_SEARCHES_SUCCESS, types.GET_SAVED_SEARCHES_FAILURE],
  },
})

export const deleteAccount = (password: string) =>
  RSAActionCreator({
    endpoint: `${indentityServiceUrl}/accounts`,
    method: 'DELETE',
    body: JSON.stringify({ password }),
    types: [types.DELETE_ACCOUNT_REQUEST, types.DELETE_ACCOUNT_SUCCESS, types.DELETE_ACCOUNT_FAILURE],
  })

export const removeDeleteAccountError = () => ({ type: types.DELETE_ACCOUNT_REMOVE_ERROR })

export const resetDownloadData = () => ({
  type: types.DOWNLOAD_ACCOUNT_DATA_RESET,
})

export const downloadAccountData = () =>
  RSAActionCreator({
    endpoint: `${indentityServiceUrl}/accounts`,
    types: [
      types.DOWNLOAD_ACCOUNT_DATA_REQUEST,
      {
        type: types.DOWNLOAD_ACCOUNT_DATA_SUCCESS,
        payload: (_action: any, _state: any, response: Response) => response.blob(),
      },
      types.DOWNLOAD_ACCOUNT_DATA_FAILURE,
    ],
  })

export const addSignupFavouriteVehicle = (vehicleId: number) => {
  return {
    type: types.ADD_SIGNUP_FAVOURITE_VEHICLE,
    payload: vehicleId,
  }
}

export const addSignupDirectFavouriteVehicle = (vehicleId: number) => {
  return {
    type: types.ADD_SIGNUP_DIRECT_FAVOURITE_VEHICLE,
    payload: vehicleId,
  }
}

const getAuthHeadersNoCache = (state: any) => {
  return {
    Authorization: `Bearer ${state.oidc.user.access_token}`,
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
  }
}

const getAuthHeaders = (state: any) => {
  return {
    Authorization: `Bearer ${state.oidc.user.access_token}`,
    'Content-Type': 'application/json',
  }
}
